/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Element } from 'react';

import React, { useEffect } from 'react';
import { Button, Col, Row } from 'reactstrap';
import Header from '#components/support/layout/Header/index.js';
import Main from '#components/support/layout/MainPageContainer/index.jsx';
import Page from '#components/support/layout/Page/index.js';
import { MODELS, UPLOAD } from '#constants/pathStrings.js';
import * as layout from '../support/reportReaderLayout.module.scss';
import { actions, eyebrow } from './UploadDecisionStep.module.scss';

type Props = {|
  routeParams: {| modelId: string |},
  navigate: Function,
  chooseUploadReport: Function,
  endCreateModelFlow: Function,
  leaveCreateModelFlowStep: Function,
|};

/*
 * The create flow's decision step (HBR-4950 #316; UI design §2.1) -- shown once the (real,
 * already-created) model exists, for orgs with extraction access only (RequireExtractionPermission
 * gates the route). "Upload a report" is the primary, default-focused path into the upload step;
 * "Start with an empty model" skips straight into the model, the same destination the old Skip
 * control used. Both choices are one-shot (decision 3): there is no in-model "upload later" entry
 * point once either is taken. Laid out like the upload and progress steps (reportReaderLayout) so
 * the three screens read as one SP3-AI-ReportReader flow (#183).
 */
const UploadDecisionStep = ({
  routeParams,
  navigate,
  chooseUploadReport,
  endCreateModelFlow,
  leaveCreateModelFlowStep,
}: Props): Element<typeof Page> => {
  const { modelId } = routeParams;

  // Covers every exit this component doesn't itself drive -- browser Back, a route change, etc.
  // (Copilot finding on PR #344): clears 'decide' unless "Upload a report" already advanced the
  // step to 'upload', in which case the reducer's own match-the-step check makes this a no-op.
  useEffect(
    () => () => leaveCreateModelFlowStep(modelId, 'decide'),
    [modelId, leaveCreateModelFlowStep]
  );

  const handleUploadReport = () => {
    chooseUploadReport(modelId);
    navigate(`/${MODELS}/${modelId}/${UPLOAD}`);
  };

  const handleStartEmptyModel = () => {
    endCreateModelFlow(modelId);
    navigate(`/${MODELS}/${modelId}`);
  };

  return (
    <Page className="model-type-page">
      <Header />
      <Main className={layout.centeredMain}>
        <Row className="justify-content-center">
          <Col md="10" lg="8" xl="7" className="col text-center">
            <p className={eyebrow}>
              SP3-AI-<em>ReportReader</em>
            </p>
            <h1 className={layout.title}>Do you want to upload a report?</h1>
            <p className={layout.intro}>
              Let SP3-AI fill in the model from a structural report, or start with an empty model
              and enter the inputs yourself.
            </p>
            <div className={actions}>
              <Button
                className="upload-report-button"
                color="primary"
                autoFocus
                onClick={handleUploadReport}
              >
                Upload a report
              </Button>
              <Button
                className="start-empty-model-button"
                color="primary"
                outline
                onClick={handleStartEmptyModel}
              >
                Start with an empty model
              </Button>
            </div>
          </Col>
        </Row>
      </Main>
    </Page>
  );
};

export default UploadDecisionStep;
